import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import FounderSection from "@/components/FounderSection";
import { APPROACH, CALENDLY_URL } from "@/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About | Business Development With Commercial Focus | Thrive Business Development"
        description="Thrive Business Development combines market understanding, structured research and practical business development experience to help companies grow with greater clarity."
      />

      <section className="relative overflow-hidden pt-40 pb-20 sm:pt-48 sm:pb-24" data-testid="about-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">About Thrive</p>
            <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Business Development With Commercial Focus
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Thrive Business Development was created to help companies take a more intelligent
              approach to growth.
            </p>
            <p className="mt-4 max-w-2xl text-base text-slate-400 leading-relaxed">
              We combine market understanding, research and practical business development thinking
              to help businesses understand their market, identify the organisations they should be
              targeting and focus their commercial effort where it has the greatest potential.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="about-why-exists">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-[260px_1fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow-blue">Why Thrive Exists</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display max-w-2xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] leading-tight text-white">
              Too much business development activity starts with a list of companies and ends with
              unfocused outreach.
            </h2>
            <p className="prose-justify mt-6 max-w-2xl text-base text-slate-400 leading-relaxed">
              Thrive starts by understanding the client, their capabilities and the markets most
              likely to need them.
            </p>
            <div className="mt-10 max-w-2xl border-l-2 border-gold/60 pl-6">
              <p className="prose-justify text-lg text-slate-300 leading-relaxed">
                Better intelligence at the start means sharper targeting, stronger conversations and
                commercial effort spent where it counts.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28 bg-navy-light/40" data-testid="about-approach">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Our Approach</p>
          </Reveal>
          <div className="mt-8 border-t border-white/10">
            {APPROACH.map((a) => (
              <Reveal key={a.num}>
                <div
                  className="group grid gap-3 border-b border-white/10 py-9 sm:grid-cols-[90px_280px_1fr] sm:items-baseline sm:gap-10"
                  data-testid={`about-approach-${a.num}`}
                >
                  <span className="font-mono text-sm text-gold/60">{a.num}</span>
                  <h3 className="font-display text-2xl font-bold tracking-[-0.03em] text-white transition-colors duration-300 group-hover:text-gold">
                    {a.title}
                  </h3>
                  <p className="prose-justify max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 max-w-2xl font-display text-xl sm:text-2xl text-slate-200 leading-relaxed">
              We believe useful market intelligence should make the next commercial decision easier.
            </p>
          </Reveal>
        </div>
      </section>

      <div id="founder" className="scroll-mt-24">
        <FounderSection />
      </div>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <h2 className="font-display max-w-xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
              A more intelligent approach starts with a conversation.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-book-call-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Call
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
